import getPageNameFromPrismicType from '~/lib/get-page-name-from-prismic-type'
import { PRISMIC_TYPES } from '~/lib/prismic-types'

import PrismicRichText from '~/components/Abstracts/Prismic'
import { BlogNavigationProps } from '~/components/Navigation/BlogNavigation'
import { ArticleCardProps } from '~/components/Slices/ArticleCard'
import { HeroTitleProps } from '~/components/Slices/HeroTitle'
import { PaginationProps } from '~/components/UI/Pagination'

import { getBlogArticles } from '~/data/blog-page-data/data'
import { DefaultPageData, SerializerParams } from '~/data/page-data/serializer'
import serializeBreadcrumb from '~/data/serialize-breadcrumb'
import serializeCardArticle from '~/data/serialize-card-article'
import serializeCustomBreadcrumb from '~/data/serialize-custom-breadcrumb'

import serializeCta from '../serialize-cta'

export const queryParamToArrayParams = (values: string | string[]) => [
  ...(typeof values === 'string' ? values.split('-') : values ?? []),
]

export interface BlogPageData extends DefaultPageData {
  hero: HeroTitleProps
  navigation?: BlogNavigationProps
  pagination?: PaginationProps
  items: ArticleCardProps[]
  previewRef?: string
}

function serializeCategories(
  data,
  locale,
  keys: string[],
): BlogNavigationProps['categories'] {
  return data?.length > 0
    ? data?.map(({ item }) => ({
        id: item?.id ?? null,
        key: item?.uid ?? null,
        value: PrismicRichText.asText(item?.data?.name) ?? null,
        checked: keys?.includes(item?.data?.uid ?? null) ?? false,
      }))
    : null
}

export function serializeItems(data, locale): ArticleCardProps[] {
  return (
    data?.map((item) =>
      serializeCardArticle(
        {
          ...item,
          link_type: 'Document',
          data: item?.data,
        },
        locale,
      ),
    ) ?? []
  )
}

export async function serializeBlogPage(
  primaryPageData,
  serializedPageData,
  { locale, previewRef, query, type }: SerializerParams,
): Promise<BlogPageData> {
  const { categories, page } = query || {}

  const navigationCategories = serializeCategories(
    primaryPageData?.categories,
    locale,
    queryParamToArrayParams(categories),
  )

  const categoriesQueries = navigationCategories.filter(
    ({ checked }) => checked,
  )

  const categoryIds = categoriesQueries.map(({ id }) => id)

  const articles = await getBlogArticles({
    page,
    locale,
    previewRef,
    categoryIds,
  })

  return {
    ...serializedPageData,
    hero: {
      title: primaryPageData?.title ?? null,
    },
    navigation: {
      title: PrismicRichText.asText(primaryPageData?.title) ?? null,
      categories: navigationCategories ?? null,
    },
    pagination: {
      maxPage: articles?.total_pages ?? 0,
    },
    items: serializeItems(articles?.results, locale),
    breadcrumb:
      serializeCustomBreadcrumb(primaryPageData, locale) ??
      serializeBreadcrumb({
        rootLink: serializeCta(
          locale,
          { type: PRISMIC_TYPES.BLOG, link_type: 'Document' },
          getPageNameFromPrismicType(type),
        ),
        locale,
      }),
    previewRef: previewRef ?? null,
  }
}
